import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import getWeek from 'date-fns/getWeek';
import enUS from 'date-fns/locale/en-US';
import { useTimeTrackingContext } from '@/hooks/useTimeTrackingContext';
import { useTimeEntriesCache } from '@/hooks/useTimeEntriesCache';
import { TimeEntry, UpdateTimeEntryInput } from '@/services/timeTracking';
import { useProjects } from '@/hooks/useProjects';
import { useHolidays } from '@/hooks/useHolidays';
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import TimeEntryForm from '@/components/TimeEntryForm';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import DatePickerDropdown from '@/components/DatePickerDropdown';
import { detectOverlappingEntries } from '@/utils/overlapDetection';
import { computeBudgetViolatingEntryIds } from '@/utils/budgetUtils';
import { getTimezoneOffsetInfo, getBrowserTimezone } from '@/utils/timezoneOptions';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Helper to create localizer with dynamic week start day
const createLocalizer = (weekStartsOn: 0 | 1) => dateFnsLocalizer({
  format: (date: Date, formatStr: string) => {
    if (formatStr === 'HH:mm') {
      return format(date, 'HH:mm');
    }
    return format(date, formatStr);
  },
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn }),
  getDay: (date: Date) => getDay(date), // 0 = Sunday, 1 = Monday, etc.
  locales: {
    'en-US': enUS,
  },
});

type CalendarView = 'day' | 'week';

interface CalendarEventResource {
  projectColor: string;
  description: string;
  location?: string;
  originalEntryId?: string;
  timezone?: string;
  isOverlapping?: boolean;
  isBudgetViolating?: boolean;
}



export default function Calendar() {
  const { updateTimeEntry, createTimeEntry, deleteTimeEntry, getCurrentDuration, error: timeTrackingError } = useTimeTrackingContext();
  const { allEntries: timeEntries, loading: cacheLoading } = useTimeEntriesCache();
  const projects = useProjects();
  const { displaySettings, timezoneSettings, effectiveWeekStartDay } = useUserSettingsContext();

  // Only active projects — used for creation and edit forms
  // (allProjects only contains active projects — archived ones are not loaded here)
  const activeProjects = useMemo(
    () => (projects.allProjects || []).filter(p => p.status === 'active' || !p.status),
    [projects.allProjects]
  );

  // Memoize localizer based on week start day
  const localizer = useMemo(() => createLocalizer(effectiveWeekStartDay), [effectiveWeekStartDay]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('week');
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [creatingEntry, setCreatingEntry] = useState<{ startTime: Date; endTime: Date } | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<TimeEntry | null>(null);
  
  // Get holidays for the current month
  const { holidays } = useHolidays(
    selectedDate.getFullYear(),
    selectedDate.getMonth() + 1
  );

  // Filter time entries for the selected date or week
  const filteredTimeEntries = useMemo(() => {
    if (calendarView === 'day') {
      const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
      const endOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);

      return timeEntries.filter((entry) => {
        const entryStart = new Date(entry.startTime);
        const entryEnd = entry.endTime ? new Date(entry.endTime) : new Date(entry.startTime);

        // Include entries that overlap with the selected day
        return entryStart <= endOfDay && entryEnd >= startOfDay;
      });
    } else {
      // Weekly view - get entries for the entire week
      const startOfWeekDate = startOfWeek(selectedDate, { weekStartsOn: effectiveWeekStartDay });
      const endOfWeekDate = new Date(startOfWeekDate);
      endOfWeekDate.setDate(endOfWeekDate.getDate() + 6);
      endOfWeekDate.setHours(23, 59, 59, 999);

      return timeEntries.filter((entry) => {
        const entryStart = new Date(entry.startTime);
        const entryEnd = entry.endTime ? new Date(entry.endTime) : new Date(entry.startTime);

        // Include entries that overlap with the selected week
        return entryStart <= endOfWeekDate && entryEnd >= startOfWeekDate;
      });
    }
  }, [timeEntries, selectedDate, calendarView, effectiveWeekStartDay]);

  // Calculate dynamic time range based on entries (excluding holidays)
  const { calendarMin, calendarMax, timeRangeKey } = useMemo(() => {
    // Default/minimum working hours (08:00 - 18:00)
    const DEFAULT_START = 8;
    const DEFAULT_END = 18;
    const PADDING_HOURS = 1;

    // Filter out entries without times and get only non-holiday entries
    const validEntries = filteredTimeEntries.filter(entry => entry.startTime);

    if (validEntries.length === 0) {
      // No entries - use default range
      return {
        calendarMin: new Date(0, 0, 0, DEFAULT_START, 0, 0),
        calendarMax: new Date(0, 0, 0, DEFAULT_END, 0, 0),
        timeRangeKey: `${DEFAULT_START}-${DEFAULT_END}`,
      };
    }

    // Calculate the view period boundaries
    let viewPeriodStart: Date;
    let viewPeriodEnd: Date;

    if (calendarView === 'day') {
      viewPeriodStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
      viewPeriodEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);
    } else {
      viewPeriodStart = startOfWeek(selectedDate, { weekStartsOn: effectiveWeekStartDay });
      viewPeriodEnd = new Date(viewPeriodStart);
      viewPeriodEnd.setDate(viewPeriodEnd.getDate() + 6);
      viewPeriodEnd.setHours(23, 59, 59, 999);
    }

    // Find earliest start and latest end times (using DISPLAYED hours within the view period)
    let earliestHour = 24;
    let latestHour = 0;
    let hasOvernightEntry = false;

    validEntries.forEach(entry => {
      const entryStart = new Date(entry.startTime);
      const entryEnd = entry.endTime ? new Date(entry.endTime) : new Date();

      // For multi-day entries, we need to check each day within the view period
      // to find the actual displayed hours
      const currentDate = new Date(viewPeriodStart);

      while (currentDate <= viewPeriodEnd) {
        const dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);

        // Check if entry overlaps with this day
        if (entryStart <= dayEnd && entryEnd >= dayStart) {
          // Calculate the displayed start/end for this day
          const displayedStart = entryStart < dayStart ? dayStart : entryStart;
          const displayedEnd = entryEnd > dayEnd ? dayEnd : entryEnd;

          const displayedStartHour = displayedStart.getHours() + displayedStart.getMinutes() / 60;
          const displayedEndHour = displayedEnd.getHours() + displayedEnd.getMinutes() / 60;

          // Track earliest and latest displayed hours
          if (displayedStartHour < earliestHour) earliestHour = displayedStartHour;
          // For end of day (23:59), treat as 24 for calculation purposes
          const effectiveEndHour = displayedEnd.getHours() === 23 && displayedEnd.getMinutes() >= 59 ? 24 : displayedEndHour;
          if (effectiveEndHour > latestHour) latestHour = effectiveEndHour;

          // Check for overnight entry (within a single day, end hour < start hour)
          if (displayedEndHour < displayedStartHour && displayedEnd.getDate() === displayedStart.getDate()) {
            hasOvernightEntry = true;
          }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    // For overnight entries, show full day
    if (hasOvernightEntry) {
      return {
        calendarMin: new Date(0, 0, 0, 0, 0, 0),
        calendarMax: new Date(0, 0, 0, 23, 59, 59),
        timeRangeKey: '0-24-overnight',
      };
    }

    // Add padding and round to nearest hour
    let minHour = Math.floor(earliestHour) - PADDING_HOURS;
    let maxHour = Math.ceil(latestHour) + PADDING_HOURS;

    // Ensure minimum range is at least DEFAULT_START to DEFAULT_END (08:00 - 18:00)
    minHour = Math.min(minHour, DEFAULT_START);
    maxHour = Math.max(maxHour, DEFAULT_END);

    // Clamp to valid hours (0-24)
    minHour = Math.max(0, minHour);
    maxHour = Math.min(24, maxHour);

    // Handle edge case where max is 24 (end of day)
    const maxDate = maxHour === 24
      ? new Date(0, 0, 0, 23, 59, 59)
      : new Date(0, 0, 0, maxHour, 0, 0);

    return {
      calendarMin: new Date(0, 0, 0, minHour, 0, 0),
      calendarMax: maxDate,
      timeRangeKey: `${minHour}-${maxHour}`,
    };
  }, [filteredTimeEntries, calendarView, selectedDate, effectiveWeekStartDay]);

  // Check if there are entries from different timezones than the browser's timezone
  const hasTimezoneMismatch = useMemo(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return filteredTimeEntries.some(entry => entry.timezone && entry.timezone !== browserTimezone);
  }, [filteredTimeEntries]);

  // Detect overlapping entries within the same project using shared utility
  // Uses "benefit of doubt" approach: minute precision for form entries, second precision for timer entries
  const overlappingEntryIds = useMemo(() => {
    return detectOverlappingEntries(timeEntries);
  }, [timeEntries]);

  // Compute budget-violating entry IDs
  const budgetViolatingEntryIds = useMemo(() => {
    if (!displaySettings.showBudgetWarnings) return new Set<string>();
    return computeBudgetViolatingEntryIds(filteredTimeEntries, activeProjects, getCurrentDuration);
  }, [filteredTimeEntries, activeProjects, displaySettings.showBudgetWarnings, getCurrentDuration]);

  // Map time entries to React Big Calendar events
  const events = useMemo(() => {
    const result: Array<{
      id: string;
      title: string;
      start: Date;
      end: Date;
      resource: {
        projectColor: string;
        description: string;
        location?: string;
        originalEntryId?: string;
        timezone?: string;
        isOverlapping?: boolean;
        isBudgetViolating?: boolean;
      };
    }> = [];

    filteredTimeEntries.forEach((entry) => {
      const project = projects.projects.find((p) => p.id === entry.projectId);
      const startTime = new Date(entry.startTime);
      // For currently running events (no endTime), extend to current time
      const endTime = entry.endTime ? new Date(entry.endTime) : new Date();
      
      if (calendarView === 'day') {
        // For daily view, adjust to show only the portion within the selected day
        const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
        const endOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);
        
        let adjustedStart = startTime;
        let adjustedEnd = endTime;
        
        // If event starts before selected day, start from beginning of selected day
        if (startTime < startOfDay) {
          adjustedStart = startOfDay;
        }
        
        // If event ends after selected day, end at end of selected day
        if (endTime > endOfDay) {
          adjustedEnd = endOfDay;
        }
        
        const timeRange = `${format(adjustedStart, 'HH:mm')} - ${format(adjustedEnd, 'HH:mm')}`;
        const projectName = project?.name || '';
        const fullTitle = projectName ? `${timeRange} [${projectName}]` : timeRange;
        
        result.push({
          id: entry.id,
          title: fullTitle,
          start: adjustedStart,
          end: adjustedEnd,
          resource: {
            projectColor: project?.color || '#3B82F6',
            description: entry.description,
            location: entry.location,
            timezone: entry.timezone,
            isOverlapping: overlappingEntryIds.has(entry.id),
            isBudgetViolating: budgetViolatingEntryIds.has(entry.id),
          },
        });
      } else {
        // For weekly view, split multi-day events into separate events for each day
        const startOfWeekDate = startOfWeek(selectedDate, { weekStartsOn: effectiveWeekStartDay });
        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setDate(endOfWeekDate.getDate() + 6);
        endOfWeekDate.setHours(23, 59, 59, 999);
        
        // Calculate the days this event spans
        const eventStartDate = new Date(startTime);
        const eventEndDate = new Date(endTime);
        
        // Get the start and end dates within the current week view
        const weekStart = new Date(Math.max(startOfWeekDate.getTime(), eventStartDate.getTime()));
        const weekEnd = new Date(Math.min(endOfWeekDate.getTime(), eventEndDate.getTime()));
        
        // Create events for each day the entry spans
        const currentDate = new Date(weekStart);
        currentDate.setHours(0, 0, 0, 0);
        
        while (currentDate <= weekEnd) {
          const dayStart = new Date(currentDate);
          const dayEnd = new Date(currentDate);
          dayEnd.setHours(23, 59, 59, 999);
          
          // Determine the actual start and end times for this day
          const dayEventStart = new Date(Math.max(dayStart.getTime(), startTime.getTime()));
          const dayEventEnd = new Date(Math.min(dayEnd.getTime(), endTime.getTime()));
          
          // Only create event if there's actual time on this day (including zero-duration events)
          if (dayEventStart <= dayEventEnd) {
            const timeRange = `${format(dayEventStart, 'HH:mm')} - ${format(dayEventEnd, 'HH:mm')}`;
            const projectName = project?.name || '';
            const fullTitle = projectName ? `${timeRange} [${projectName}]` : timeRange;
            
            result.push({
              id: `${entry.id}-${format(currentDate, 'yyyy-MM-dd')}`, // Make ID unique for each day
              title: fullTitle,
              start: dayEventStart,
              end: dayEventEnd,
              resource: {
                projectColor: project?.color || '#3B82F6',
                description: entry.description,
                location: entry.location,
                originalEntryId: entry.id, // Keep original ID for editability
                timezone: entry.timezone,
                isOverlapping: overlappingEntryIds.has(entry.id),
                isBudgetViolating: budgetViolatingEntryIds.has(entry.id),
              } as CalendarEventResource,
            });
          }
          
          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    // Add holiday events
    holidays.forEach((holiday) => {
      const holidayDate = new Date(holiday.date);
      const holidayStart = new Date(holidayDate);
      holidayStart.setHours(0, 0, 0, 0);
      const holidayEnd = new Date(holidayDate);
      holidayEnd.setHours(23, 59, 59, 999);

      // Check if holiday falls within the current view
      if (calendarView === 'day') {
        const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0);
        const endOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59);
        
        if (holidayDate >= startOfDay && holidayDate <= endOfDay) {
          result.push({
            id: `holiday-${holiday.date}`,
            title: `🎉 ${holiday.name}`,
            start: holidayStart,
            end: holidayEnd,
            resource: {
              projectColor: holiday.color || '#ef4444',
              description: holiday.name,
              location: undefined,
            } as CalendarEventResource,
          });
        }
      } else {
        // Weekly view
        const startOfWeekDate = startOfWeek(selectedDate, { weekStartsOn: effectiveWeekStartDay });
        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setDate(endOfWeekDate.getDate() + 6);
        endOfWeekDate.setHours(23, 59, 59, 999);

        if (holidayDate >= startOfWeekDate && holidayDate <= endOfWeekDate) {
          result.push({
            id: `holiday-${holiday.date}`,
            title: `🎉 ${holiday.name}`,
            start: holidayStart,
            end: holidayEnd,
            resource: {
              projectColor: holiday.color || '#ef4444',
              description: holiday.name,
              location: undefined,
            } as CalendarEventResource,
          });
        }
      }
    });
    
    return result;
  }, [filteredTimeEntries, projects.projects, selectedDate, calendarView, holidays, overlappingEntryIds, budgetViolatingEntryIds, effectiveWeekStartDay]);

  // Handle event click
  const handleEventClick = (event: { id: string; resource?: CalendarEventResource }) => {
    // Don't allow editing holiday events
    if (event.id.startsWith('holiday-')) {
      return;
    }
    
    // Use originalEntryId from resource if available, otherwise fall back to event.id
    const entryId = event.resource?.originalEntryId || event.id;
    const entry = timeEntries.find(entry => entry.id === entryId);
    if (entry) {
      setEditingEntry(entry);
    }
  };

  // Handle update - wrapper for TimeEntryForm
  const handleUpdate = async (data: UpdateTimeEntryInput) => {
    if (editingEntry) {
      try {
        await updateTimeEntry(editingEntry.id, data);
        setEditingEntry(null);
      } catch {
        // Error is handled by the hook
      }
    }
  };

  // Handle slot selection - open create form with pre-filled times
  const handleSelectSlot = (slotInfo: { start: Date; end: Date; action: string }) => {
    // Only handle click actions, not drag selections for now
    if (slotInfo.action === 'click' || slotInfo.action === 'select') {
      // Set end time to 1 hour after start if same time (single click)
      const endTime = slotInfo.start.getTime() === slotInfo.end.getTime()
        ? new Date(slotInfo.start.getTime() + 60 * 60 * 1000) // Add 1 hour
        : slotInfo.end;
      setCreatingEntry({ startTime: slotInfo.start, endTime });
    }
  };

  // Handle create - wrapper for TimeEntryForm
  const handleCreate = async (data: { description?: string; projectId?: string; startTime?: string; endTime?: string; location?: string; timezone?: string }) => {
    if (!data.description || !data.startTime || !data.endTime) return;
    try {
      await createTimeEntry({
        description: data.description,
        projectId: data.projectId,
        startTime: data.startTime,
        endTime: data.endTime,
        location: data.location,
        timezone: data.timezone
      });
      setCreatingEntry(null);
    } catch {
      // Error is handled by the hook
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deletingEntry) {
      try {
        await deleteTimeEntry(deletingEntry.id);
        setDeletingEntry(null);
      } catch {
        // Error is handled by the hook
      }
    }
  };

  // Handle delete button click on event
  const handleDeleteClick = (entryId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening edit modal
    const entry = timeEntries.find(entry => entry.id === entryId);
    if (entry) {
      setDeletingEntry(entry);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">View:</span>
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => {
                    setCalendarView('day');
                    setSelectedDate(new Date()); // Set to current day when switching to day view
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    calendarView === 'day'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Day
                </button>
                <button
                  onClick={() => setCalendarView('week')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    calendarView === 'week'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
              </div>
            </div>
            
            <DatePickerDropdown
              id="date-picker"
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              calendarView={calendarView}
              onViewChange={setCalendarView}
              minWidth="min-w-[220px]"
              weekStartsOn={effectiveWeekStartDay}
            />
          </div>
        </div>
      </div>

      {projects.error ? (
        <div className="text-center py-8 text-red-600">Error loading projects</div>
      ) : cacheLoading ? (
        <div className="text-center py-8">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="min-h-[600px]">
            <div data-testid="calendar">
              {timeEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No time entries for this date</div>
              ) : (
                <>
                  <style>
                    {`
                      /* Remove separate scrollbar and iframe appearance */
                      .rbc-calendar {
                        font-family: inherit !important;
                        border: none !important;
                        background: transparent !important;
                      }
                      
                      /* Remove default borders and shadows */
                      .rbc-time-view {
                        border: none !important;
                        box-shadow: none !important;
                        background: transparent !important;
                      }
                      
                      .rbc-time-header {
                        background: transparent !important;
                        border: none !important;
                        border-bottom: 1px solid #e5e7eb !important;
                      }
                      
                      .rbc-time-header-content {
                        border: none !important;
                        /* Remove transform to align with content */
                      }
                      
                      .rbc-time-content {
                        background: transparent !important;
                        border: none !important;
                        overflow: visible !important;
                      }
                      
                      .rbc-time-gutter {
                        background: transparent !important;
                        border: none !important;
                        border-right: 1px solid #e5e7eb !important;
                      }
                      
                      .rbc-time-gutter .rbc-label {
                        color: #6b7280 !important;
                        font-weight: 400 !important;
                        background: transparent !important;
                        font-size: 10px !important;
                      }
                      
                      .rbc-timeslot-group {
                        border-bottom: 1px solid #f3f4f6 !important;
                      }
                      
                      .rbc-time-slot {
                        background: transparent !important;
                      }
                      
                      /* Event styling */
                      .rbc-event {
                        border: none !important;
                        border-radius: 4px !important;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
                      }
                      
                      .rbc-event-content {
                        color: white !important;
                        font-weight: 500 !important;
                      }
                      
                      /* Holiday event styling - base styles, colors will be overridden by eventPropGetter */
                      .rbc-event[data-event-id^="holiday-"] {
                        border: 2px solid !important;
                        box-shadow: 0 2px 4px !important;
                      }
                      
                      .rbc-event[data-event-id^="holiday-"] .rbc-event-content {
                        color: white !important;
                        font-weight: 600 !important;
                        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3) !important;
                      }
                      
                      /* Header styling - unified approach */
                      .rbc-time-header-content .rbc-header {
                        border: none !important;
                        background: transparent !important;
                        color: #374151 !important;
                        font-weight: 500 !important;
                        border-right: 1px solid #e5e7eb !important;
                        padding: 8px 4px !important;
                        font-size: 12px !important;
                        width: calc(100% / 7) !important;
                        box-sizing: border-box !important;
                      }
                      
                      /* Today and current time */
                      .rbc-today {
                        background-color: rgba(59, 130, 246, 0.05) !important;
                      }
                      
                      .rbc-current-time-indicator {
                        background-color: #ef4444 !important;
                        height: 2px !important;
                      }
                      
                      /* Remove scrollbars */
                      .rbc-time-content,
                      .rbc-time-view,
                      .rbc-calendar {
                        overflow: visible !important;
                      }
                      
                      /* Remove any scroll containers */
                      .rbc-time-content .rbc-timeslot-group {
                        overflow: visible !important;
                      }
                      
                      /* Multi-day event handling in day view */
                      .rbc-event.rbc-event-continues-prior {
                        border-top-left-radius: 0 !important;
                        border-bottom-left-radius: 0 !important;
                      }
                      
                      .rbc-event.rbc-event-continues-after {
                        border-top-right-radius: 0 !important;
                        border-bottom-right-radius: 0 !important;
                      }
                      
                      /* Ensure multi-day events show in the time grid */
                      .rbc-time-view .rbc-event {
                        position: absolute !important;
                        z-index: 1 !important;
                      }
                      
                      /* Hide the default time range display in rbc-event-label */
                      .rbc-event .rbc-event-label {
                        display: none !important;
                      }
                      
                      /* Event styling for no-overlap layout */
                      .rbc-event {
                        min-height: 18px !important;
                        opacity: 0.95 !important;
                        margin-right: 1px !important;
                      }

                      /* Adjacent events get proper spacing */
                      .rbc-events-container {
                        margin-right: 1px !important;
                      }

                      /* Make overlapping events more distinct */
                      .rbc-event.rbc-event-overlaps {
                        opacity: 0.9 !important;
                      }

                      /* Hover effect to bring event to front */
                      .rbc-event:hover {
                        z-index: 10 !important;
                        opacity: 1 !important;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
                      }
                      
                      /* Make text more readable on short events */
                      .rbc-event .text-xs {
                        line-height: 1.2 !important;
                      }
                      
                      /* Weekly view specific styles */
                      .rbc-time-view .rbc-time-header {
                        border-bottom: 1px solid #e5e7eb !important;
                      }
                      
                      /* Ensure header and content columns align properly */
                      .rbc-time-view .rbc-time-header-content {
                        border-left: none !important; /* Remove left border to prevent double line */
                      }
                      
                      .rbc-time-view .rbc-time-header-content .rbc-header {
                        border-right: 1px solid #e5e7eb !important;
                        padding: 8px 4px !important;
                        font-size: 12px !important;
                        font-weight: 500 !important;
                        color: #374151 !important;
                        width: calc(100% / 7) !important; /* Ensure equal width for 7 days */
                        box-sizing: border-box !important;
                        position: relative !important; /* Ensure proper positioning */
                        /* Remove transform for consistent alignment */
                      }
                      
                      .rbc-time-view .rbc-time-header-content .rbc-header:last-child {
                        border-right: none !important;
                      }
                      
                      /* Today column highlighting in week view */
                      .rbc-time-view .rbc-time-header-content .rbc-header.rbc-today {
                        background-color: rgba(59, 130, 246, 0.05) !important;
                        font-weight: 600 !important;
                      }
                      
                      /* Week view time slots - ensure alignment with headers */
                      .rbc-time-view .rbc-timeslot-group {
                        border-bottom: 1px solid #f3f4f6 !important;
                        border-left: none !important; /* Remove left border to prevent double line */
                      }
                      
                      .rbc-time-view .rbc-timeslot-group .rbc-time-slot {
                        border-right: 1px solid #e5e7eb !important; /* Use same color as header for consistency */
                        width: calc(100% / 7) !important; /* Match header width */
                        box-sizing: border-box !important;
                        position: relative !important; /* Ensure proper positioning */
                        /* Remove transform for consistent alignment */
                      }
                      
                      .rbc-time-view .rbc-timeslot-group .rbc-time-slot:last-child {
                        border-right: none !important;
                      }
                      
                      /* Today column in week view */
                      .rbc-time-view .rbc-timeslot-group .rbc-time-slot.rbc-today {
                        background-color: rgba(59, 130, 246, 0.05) !important;
                      }
                      
                      /* Today background element - ensure it has left border */
                      .rbc-time-view .rbc-day-bg.rbc-today {
                        border-left: 1px solid #e5e7eb !important;
                      }
                      
                      /* Adjust left border of day background elements */
                      .rbc-time-view .rbc-day-bg {
                        border-left: 1px solid #e5e7eb !important;
                        /* Remove transform for consistent alignment */
                      }
                      
                      /* Force perfect alignment by targeting the table structure */
                      .rbc-time-view .rbc-time-header-content table {
                        border-collapse: collapse !important;
                        width: 100% !important;
                        table-layout: fixed !important;
                      }
                      
                      .rbc-time-view .rbc-time-content table {
                        border-collapse: collapse !important;
                        width: 100% !important;
                        table-layout: fixed !important;
                      }
                      
                      /* Ensure table cells have no extra spacing */
                      .rbc-time-view .rbc-time-header-content table td,
                      .rbc-time-view .rbc-time-content table td {
                        padding: 0 !important;
                        margin: 0 !important;
                        border-spacing: 0 !important;
                        border: none !important;
                      }
                      
                      /* Apply borders at the table cell level for perfect alignment */
                      .rbc-time-view .rbc-time-header-content table td:not(:last-child) {
                        border-right: 1px solid #e5e7eb !important;
                      }
                      
                      .rbc-time-view .rbc-time-content table td:not(:last-child) {
                        border-right: 1px solid #e5e7eb !important;
                      }
                      
                      /* Remove conflicting borders from individual elements */
                      .rbc-time-view .rbc-time-header-content .rbc-header {
                        border: none !important;
                        border-left: 1px solid #e5e7eb !important;
                      }
                      
                      /* Fix alignment for header cells and all-day cells */
                      .rbc-time-view .rbc-row.rbc-time-header-cell {
                        border-right: 1px solid #e5e7eb !important;
                        /* Remove transform for consistent alignment */
                      }
                      
                      .rbc-time-view .rbc-allday-cell {
                        border-right: 1px solid #e5e7eb !important;
                        /* Remove transform for consistent alignment */
                      }
                      
                      .rbc-time-view .rbc-timeslot-group .rbc-time-slot {
                        border: none !important;
                      }
                      
                      /* Ensure time gutter doesn't interfere with alignment */
                      .rbc-time-view .rbc-time-gutter {
                        border-right: none !important; /* Remove time gutter border to prevent double line */
                        width: 60px !important; /* Fixed width for time gutter */
                        min-width: 60px !important;
                        max-width: 60px !important;
                      }
                      
                      /* Force proper table layout for alignment */
                      .rbc-time-view .rbc-time-content {
                        table-layout: fixed !important;
                        width: 100% !important;
                      }
                      
                      .rbc-time-view .rbc-time-header-content {
                        table-layout: fixed !important;
                        width: 100% !important;
                      }
                      
                      /* Ensure all table cells have consistent sizing */
                      .rbc-time-view .rbc-time-header-content table {
                        width: 100% !important;
                        table-layout: fixed !important;
                      }
                      
                      .rbc-time-view .rbc-time-content table {
                        width: 100% !important;
                        table-layout: fixed !important;
                      }
                      
                      /* Ensure table cells don't have extra spacing */
                      .rbc-time-view .rbc-time-header-content td,
                      .rbc-time-view .rbc-time-content td {
                        padding: 0 !important;
                        margin: 0 !important;
                        border-spacing: 0 !important;
                      }
                      
                      /* First column borders for consistent alignment */
                      .rbc-time-view .rbc-time-header-content .rbc-header:first-child {
                        border-left: 1px solid #e5e7eb !important;
                      }
                      
                      .rbc-time-view .rbc-timeslot-group .rbc-time-slot:first-child {
                        border-left: 1px solid #e5e7eb !important;
                      }
                      
                      
                      
                      

                    `}
                  </style>
                  <BigCalendar
                    key={`calendar-${timeRangeKey}`}
                    localizer={localizer}
                    events={events}
                    date={selectedDate}
                    onNavigate={(newDate) => setSelectedDate(newDate)}
                    view={calendarView === 'day' ? Views.DAY : Views.WEEK}
                    onView={(newView) => setCalendarView(newView === Views.DAY ? 'day' : 'week')}
                    views={['day', 'week']}
                    step={60}
                    timeslots={1}
                    {...(!hasTimezoneMismatch && { min: calendarMin, max: calendarMax })}
                    onSelectEvent={handleEventClick}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    dayLayoutAlgorithm="no-overlap"
                    eventPropGetter={(event) => {
                      const style: React.CSSProperties = {
                        backgroundColor: event.resource?.projectColor || '#3B82F6',
                      };
                      
                      // Add holiday-specific styling
                      if (event.id.startsWith('holiday-')) {
                        const holidayColor = event.resource?.projectColor || '#ef4444';
                        style.background = `linear-gradient(135deg, ${holidayColor}, ${holidayColor}dd)`;
                        style.border = `2px solid ${holidayColor}`;
                        style.boxShadow = `0 2px 4px ${holidayColor}50`;
                      }
                      
                      return { style };
                    }}
                    formats={{
                      timeGutterFormat: 'HH:mm',
                      eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => {
                        return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
                      },
                    }}
                    components={{
                      event: ({ event }: { event: { id: string; title: string; start: Date; end: Date; resource?: { projectColor?: string; description?: string; location?: string; originalEntryId?: string; timezone?: string; isOverlapping?: boolean; isBudgetViolating?: boolean } } }) => {
                        const description = event.resource?.description;
                        const location = event.resource?.location;
                        const timezone = event.resource?.timezone;
                        const isOverlapping = event.resource?.isOverlapping;
                        const isBudgetViolating = event.resource?.isBudgetViolating;
                        const isHoliday = event.id.startsWith('holiday-');
                        // Get the actual entry ID (for multi-day events, use originalEntryId)
                        const entryId = event.resource?.originalEntryId || event.id;
                        let tooltipText = event.title;
                        if (description) tooltipText += `\n${description}`;
                        if (location) tooltipText += `\n🏢 ${location}`;
                        if (isOverlapping) tooltipText += `\n⚠️ Overlaps with another entry`;
                        if (isBudgetViolating) tooltipText += `\nOut of budget`;

                        // Calculate event duration in minutes
                        const durationMs = event.end.getTime() - event.start.getTime();
                        const durationMinutes = Math.round(durationMs / (1000 * 60));

                        // Only show description if event is long enough (more than 15 minutes)
                        const shouldShowDescription = description && durationMinutes > 15;
                        // Show trash icon for all non-holiday events
                        const shouldShowTrashIcon = !isHoliday;

                        // Check if timezone indicator should be shown
                        // Use default timezone for legacy entries without timezone (same as TimezoneIndicator)
                        const DEFAULT_TIMEZONE = 'Europe/Berlin';
                        const browserTimezone = getBrowserTimezone();
                        const effectiveTimezone = timezone || DEFAULT_TIMEZONE;
                        const showTimezoneIcon = displaySettings.showTimezoneIndicator &&
                          effectiveTimezone !== browserTimezone &&
                          !isHoliday;
                        const formatTimezone = (tz: string) => tz.replace(/_/g, ' ');

                        // Get timezone offset info for directional icon and tooltip
                        const timezoneOffsetInfo = showTimezoneIcon
                          ? getTimezoneOffsetInfo(effectiveTimezone, browserTimezone, event.start)
                          : null;
                        const timezoneTooltip = timezoneOffsetInfo
                          ? timezoneOffsetInfo.direction === 'same'
                            ? `Recorded in ${formatTimezone(effectiveTimezone)}`
                            : `Recorded in ${formatTimezone(effectiveTimezone)} (${timezoneOffsetInfo.formattedDifference})`
                          : '';

                        // Check if overlap indicator should be shown
                        const showOverlapIcon = displaySettings.showOverlapIndicator &&
                          isOverlapping &&
                          !isHoliday;

                        // Check if budget violation indicator should be shown
                        const showBudgetIcon = displaySettings.showBudgetWarnings &&
                          isBudgetViolating &&
                          !isHoliday;

                        return (
                          <div
                            data-testid={`event-${event.id}`}
                            data-original-entry-id={entryId}
                            data-project-color={event.resource?.projectColor}
                            data-start={event.start.toISOString()}
                            data-end={event.end.toISOString()}
                            className="h-full flex flex-col justify-start p-1 cursor-pointer group relative"
                            title={tooltipText}
                          >
                            <div className="text-xs font-medium text-white truncate pr-4 flex items-center gap-1">
                              {showTimezoneIcon && timezoneOffsetInfo && (
                                <span
                                  className={`inline-flex items-center justify-center w-3 h-3 flex-shrink-0 ${
                                    timezoneOffsetInfo.direction === 'same' ? 'text-white/60' : 'text-white'
                                  }`}
                                  title={timezoneTooltip}
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    {/* Clock circle */}
                                    <circle cx="12" cy="12" r="8" />
                                    {/* Clock hands */}
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 8v4l2.5 2.5"
                                    />
                                    {timezoneOffsetInfo.direction === 'behind' ? (
                                      // Counter-clockwise arrow (timezone is behind browser = earlier time)
                                      <>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v4h4" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8A9 9 0 0112 3" />
                                      </>
                                    ) : timezoneOffsetInfo.direction === 'ahead' ? (
                                      // Clockwise arrow (timezone is ahead of browser = later time)
                                      <>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 4v4h-4" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 8A9 9 0 0012 3" />
                                      </>
                                    ) : null /* 'same' direction - no arrow, just clock */}
                                  </svg>
                                </span>
                              )}
                              {showOverlapIcon && (
                                <span
                                  className="inline-flex items-center justify-center w-3 h-3 text-white flex-shrink-0"
                                  title="This entry overlaps with another entry in the same project"
                                  data-testid={`overlap-indicator-${event.id}`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              {showBudgetIcon && (
                                <span
                                  className="inline-flex items-center justify-center w-3 h-3 text-white flex-shrink-0"
                                  title="This entry's day is out of budget"
                                  data-testid={`budget-indicator-${entryId}`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              <span className="truncate">{event.title}</span>
                            </div>
                            {shouldShowDescription && (
                              <div className="text-xs text-white/80 truncate mt-0.5 pr-4">
                                {description}
                              </div>
                            )}
                            {/* Trash icon - shown on hover for non-holiday events */}
                            {shouldShowTrashIcon && (
                              <button
                                onClick={(e) => handleDeleteClick(entryId, e)}
                                className="absolute top-0.5 right-0.5 p-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto hover:bg-white/20"
                                title="Delete entry"
                              >
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      },
                      toolbar: ({ date, onNavigate }: { date: Date; onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void }) => {
                        const handleNavigation = (action: 'PREV' | 'NEXT' | 'TODAY') => {
                          onNavigate(action);
                          if (action === 'PREV') {
                            const prevDate = new Date(date);
                            if (calendarView === 'day') {
                              prevDate.setDate(prevDate.getDate() - 1);
                            } else {
                              prevDate.setDate(prevDate.getDate() - 7);
                            }
                            setSelectedDate(prevDate);
                          } else if (action === 'NEXT') {
                            const nextDate = new Date(date);
                            if (calendarView === 'day') {
                              nextDate.setDate(nextDate.getDate() + 1);
                            } else {
                              nextDate.setDate(nextDate.getDate() + 7);
                            }
                            setSelectedDate(nextDate);
                          } else if (action === 'TODAY') {
                            setSelectedDate(new Date());
                          }
                        };
                        
                        let titleText: string;
                        let subtitleText: string;
                        
                        if (calendarView === 'day') {
                          const dayName = format(date, 'EEEE');
                          const dayDate = format(date, 'MMMM d, yyyy');
                          titleText = dayName;
                          subtitleText = dayDate;
                        } else {
                          const startOfWeekDate = startOfWeek(date, { weekStartsOn: effectiveWeekStartDay });
                          const endOfWeekDate = new Date(startOfWeekDate);
                          endOfWeekDate.setDate(endOfWeekDate.getDate() + 6);

                          const weekNumber = getWeek(date, { weekStartsOn: effectiveWeekStartDay });
                          const weekRange = `${format(startOfWeekDate, 'MMM d')} - ${format(endOfWeekDate, 'MMM d, yyyy')}`;
                          titleText = `Week ${weekNumber}`;
                          subtitleText = weekRange;
                        }
                        
                        return (
                          <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <button
                              onClick={() => handleNavigation('PREV')}
                              className="text-gray-600 hover:text-gray-900 transition-colors"
                            >
                              ←
                            </button>
                            <div className="text-center">
                              <div className="text-lg font-semibold text-gray-900">{titleText}</div>
                              <div className="text-sm text-gray-600">{subtitleText}</div>
                            </div>
                            <button
                              onClick={() => handleNavigation('NEXT')}
                              className="text-gray-600 hover:text-gray-900 transition-colors"
                            >
                              →
                            </button>
                          </div>
                        );
                      },
                    }}


                    showMultiDayTimes={false}
                    allDayAccessor={() => false}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Error for time tracking */}
      {(!cacheLoading && !projects.error && timeTrackingError) && (
        <div className="text-center py-8 text-red-600">Error loading time entries</div>
      )}
      {/* Edit Modal */}
      {editingEntry && createPortal(
        <TimeEntryForm
          mode="edit"
          entry={{ ...editingEntry, isRunning: !editingEntry.endTime }}
          projects={activeProjects}
          onSubmit={handleUpdate}
          onCancel={() => setEditingEntry(null)}
          loading={false}
          weekStartsOn={effectiveWeekStartDay}
        />,
        document.body
      )}
      {/* Create Modal */}
      {creatingEntry && createPortal(
        <TimeEntryForm
          mode="create"
          projects={activeProjects}
          onSubmit={handleCreate}
          onCancel={() => setCreatingEntry(null)}
          loading={false}
          initialStartTime={creatingEntry.startTime}
          initialEndTime={creatingEntry.endTime}
          preferredTimezone={timezoneSettings.preferredTimezone}
          weekStartsOn={effectiveWeekStartDay}
        />,
        document.body
      )}
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!deletingEntry}
        title="Delete Time Entry"
        message={`Are you sure you want to delete "${deletingEntry?.description}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => { handleDeleteConfirm(); }}
        onCancel={() => setDeletingEntry(null)}
        variant="danger"
      />
    </div>
  );
} 